/**
 * Agent-runtime product events — the load-bearing five (spec 68 §6.6.3).
 *
 * These are emitted from the `apps/webhooks` persist-audit handler
 * (apps/webhooks/src/routes/agent-runtime.ts), 1:1 with an
 * `agent_audit.audit_kind` write, so they can never be lost to a closed
 * browser tab.
 *
 * AUDIT-KIND RECONCILIATION (gap-68, revised CON-62). The spec's §6.6.3
 * emit-site column names two audit kinds; the code's enum
 * (agent-runtime.ts persistAuditBody) is the source of truth:
 *
 *   spec says          →  CODE actually uses (encoded here)
 *   ───────────────────────────────────────────────────────
 *   tool.call          →  tool.call   (spec 106 §7.1 — one row per
 *                         model-requested tool call, every source)
 *   spend_cap.breached →  run.budget_blocked   (see billing.ts)
 *
 * Until CON-62 the map keyed `agent_run.tool_called` off `skill.invoke`,
 * which only skill-backed calls write (and write in ADDITION to
 * `tool.call`): Zero built-ins, memory and persona calls were invisible
 * to the product event, and mapping both kinds would double-count
 * skills. The event is keyed off the superset and only the superset.
 *
 * `AUDIT_KIND_TO_EVENT` below maps the real persist-audit kinds to the
 * Amplitude event name the webhooks handler `track()`s. `run.finished`
 * splits into completed/failed by the run's terminal status, so it is
 * intentionally NOT in this static map (the handler branches on status).
 */
import { z } from 'zod';
import {
  DEFAULT_SCHEMA_VER,
  id,
  microusd,
  runFailStatusEnum,
  surfaceEnum,
  toolOutcomeEnum,
  toolSourceEnum,
  triggerKindEnum,
} from './_shared.js';

export const agentRunEvents = {
  'agent_run.started': {
    schema: z.object({
      account_id: id,
      project_id: id.optional(),
      agent_id: id,
      run_id: id,
      thread_id: id.optional(),
      trigger_kind: triggerKindEnum,
      surface: surfaceEnum,
    }),
    schemaVer: DEFAULT_SCHEMA_VER,
  },
  'agent_run.model_called': {
    schema: z.object({
      account_id: id,
      run_id: id,
      agent_id: id,
      model_slug: id,
      hop: z.number().int().nonnegative(),
      finish_reason: z.string().optional(),
      input_tokens: z.number().int().nonnegative().optional(),
      output_tokens: z.number().int().nonnegative().optional(),
      cache_read_input_tokens: z.number().int().nonnegative().optional(),
      cache_creation_input_tokens: z.number().int().nonnegative().optional(),
      is_byok: z.boolean(),
    }),
    schemaVer: DEFAULT_SCHEMA_VER,
  },
  // 2-0-0 (CON-62): keyed off the spec 106 `tool.call` row, so the
  // identity is the model-facing tool name (`tool_slug` — the scrubber
  // redacts every `*name` key, same reason `mcp.tool_called` uses it) +
  // `source` (zero / skill / integration / builtin) rather than a skill
  // FQN — built-ins have none. `skill_fqn` stays optional for skill-backed
  // calls that resolve one.
  'agent_run.tool_called': {
    schema: z.object({
      account_id: id,
      run_id: id,
      agent_id: id,
      tool_slug: z.string().min(1),
      source: toolSourceEnum,
      skill_fqn: z.string().min(1).optional(),
      outcome: toolOutcomeEnum,
      error_code: z.string().min(1).optional(),
      duration_ms: z.number().nonnegative().optional(),
    }),
    schemaVer: '2-0-0',
  },
  'agent_run.completed': {
    schema: z.object({
      account_id: id,
      run_id: id,
      agent_id: id,
      duration_ms: z.number().nonnegative(),
      hops: z.number().int().nonnegative(),
      cost_microusd: microusd,
      total_input_tokens: z.number().int().nonnegative().optional(),
      total_output_tokens: z.number().int().nonnegative().optional(),
    }),
    schemaVer: DEFAULT_SCHEMA_VER,
  },
  'agent_run.failed': {
    schema: z.object({
      account_id: id,
      run_id: id,
      agent_id: id,
      status: runFailStatusEnum,
      error_code: z.string().min(1),
      error_class: z.string().min(1),
      duration_ms: z.number().nonnegative(),
      hops: z.number().int().nonnegative().optional(),
    }),
    schemaVer: DEFAULT_SCHEMA_VER,
  },
} as const;

/**
 * The persist-audit `kind` → Amplitude event-name map used by the pass-2
 * wiring in apps/webhooks. Uses the CODE's real audit kinds.
 * `run.finished` is handled separately (branch on terminal status →
 * `agent_run.completed` | `agent_run.failed`).
 */
export const AUDIT_KIND_TO_EVENT = {
  'run.started': 'agent_run.started',
  'model.call': 'agent_run.model_called',
  'tool.call': 'agent_run.tool_called', // NOT skill.invoke — see the header
} as const satisfies Record<string, keyof typeof agentRunEvents>;
